/**
 * Shared Row and HeadRow components for inline detail sub-tables.
 *
 * Centralized here to avoid copy-pasting the same flex-row + column-width
 * alignment logic across StockTab, PriceListTab, and RulesTab.
 *
 * See: docs/frontend/generic-list-system/inline-detail-panel-sub-table-system.md
 */

import { cn } from "@/lib/utils";
import { SUB_TABLE_HEAD_ROW_CLASS } from "./useTableStyles";

export interface SubTableRowProps {
  columnWidths: number[];
  cells: Record<number, React.ReactNode>;
  className?: string;
  cellClass: string;
  cellExtraClass?: Record<number, string>;
  minHeight: number;
}

export function SubTableRow({
  columnWidths,
  cells,
  className,
  cellClass,
  cellExtraClass,
  minHeight,
}: SubTableRowProps) {
  return (
    <div className={className} style={{ display: "flex", minHeight }}>
      {columnWidths.map((w, i) => (
        <div
          key={i}
          className={cn(cellClass, cellExtraClass?.[i])}
          style={{ width: w, minWidth: w, maxWidth: w, boxSizing: "border-box" }}
        >
          {cells[i] ?? null}
        </div>
      ))}
    </div>
  );
}

export interface SubTableHeadRowProps {
  columnWidths: number[];
  cells: Record<number, { label: string; align?: "right" }>;
  headClass: string;
  minHeight: number;
}

export function SubTableHeadRow({
  columnWidths,
  cells,
  headClass,
  minHeight,
}: SubTableHeadRowProps) {
  return (
    <div
      className={SUB_TABLE_HEAD_ROW_CLASS}
      style={{ display: "flex", minHeight }}
    >
      {columnWidths.map((w, i) => {
        const cell = cells[i];
        return (
          <div
            key={i}
            className={cn(headClass, cell?.align === "right" && "text-right")}
            style={{ width: w, minWidth: w, maxWidth: w, boxSizing: "border-box" }}
          >
            {cell?.label ?? null}
          </div>
        );
      })}
    </div>
  );
}
